// Copyright (c) Ergoss Logiciels - All rights reserved - Proprietary and confidential
import { Injectable, computed, inject, linkedSignal } from '@angular/core';
import { NetworkService } from '@core/services/network.service';

export interface VaultServe {
  cacheKey: string;
  capturedAt: number;
}

/**
 * B3 — the freshness side channel (EF-4.2 / idea I1): the offline read path
 * serves synthesized responses that stores cannot tell from the network's —
 * deliberately so (EF-4.1). This service is the one place that CAN tell:
 * the interceptor reports every vault serve here, and the connectivity chip
 * surfaces the capture time, so no stale data is ever presented as current
 * (the addendum's non-negotiable honesty principle).
 *
 * Tracking is per offline episode: serves accumulate while unreachable and are
 * cleared on each transition BACK to reachable — not "while online", which is a
 * different and untrue claim (see `serves`). `oldestServedAt` drives the display:
 * "data as of <T>" must promise no more than the STALEST thing on screen.
 */
@Injectable({ providedIn: 'root' })
export class OfflineFreshnessService {
  private readonly network = inject(NetworkService);
  /**
   * Serves accumulate for as long as the app is unreachable, and are dropped on each transition
   * back to reachable — a stamp from the previous episode would be a lie about the new one. Keyed
   * on reachability itself, so reporting a serve never races the clear.
   */
  private readonly _serves = linkedSignal<boolean, VaultServe[]>({
    source: () => this.network.isOnline(),
    computation: (online, previous) => (online ? [] : (previous?.value ?? [])),
  });

  /**
   * Vault serves of the current offline episode. NOT "empty while online": the clear fires on the
   * transition to reachable, so a serve can legitimately land afterwards and stay. The interceptor
   * decides to serve from the vault synchronously but reports after an async IndexedDB read, so a
   * report can arrive once the app is already back online.
   *
   * Keeping it is the honest outcome, not a leak. Nothing in the app refetches on reconnect, so
   * that vault content is still on screen; surfacing its capture time is exactly the promise this
   * service exists to keep. The connectivity details row is deliberately not gated on `isOnline`
   * for the same reason.
   */
  readonly serves = this._serves.asReadonly();

  /** Epoch ms of the STALEST content served this offline episode, or null. */
  readonly oldestServedAt = computed(() => {
    const serves = this._serves();
    return serves.length ? Math.min(...serves.map((s) => s.capturedAt)) : null;
  });

  /** Called by the offline-cache interceptor on every vault serve. */
  reportVaultServe(cacheKey: string, capturedAt: number): void {
    this._serves.update((s) => [...s, { cacheKey, capturedAt }]);
  }
}
